import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Bell, Coffee, Mail, ShieldCheck } from "lucide-react";
import { HrmsEmailSettings } from "@/components/admin/HrmsEmailSettings";
import { enableDesktopNotifications } from "@/lib/push";

export default function NotificationSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/notification-settings");
      setS(data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notification-settings", s);
      setS(data);
      toast.success("Notification settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const enableDesktop = async () => {
    if (!("Notification" in window)) return toast.error("Desktop notifications are not supported in this browser");
    const perm = await enableDesktopNotifications();
    if (perm === "granted") toast.success("Desktop notifications enabled for this browser");
    else toast.error(`Permission ${perm}`);
  };

  if (!s) return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;

  const upd = (k, v) => setS({ ...s, [k]: v });

  return (
    <div>
      <PageHeader title="Notification Settings" subtitle="Configure reminders, break threshold and daily reports (Super Admin)" />

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><Coffee className="h-4 w-4"/> Break monitoring</div>
          <div>
            <Label>Break threshold (minutes, 30–45)</Label>
            <Input type="number" min={30} max={45} value={s.break_threshold_minutes}
                   onChange={(e)=>upd("break_threshold_minutes", Number(e.target.value))}
                   data-testid="break-threshold" />
          </div>
          <div>
            <Label>Repeat every (minutes, 0 = no repeat)</Label>
            <Input type="number" min={0} value={s.break_repeat_minutes}
                   onChange={(e)=>upd("break_repeat_minutes", Number(e.target.value))} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <div className="text-sm">Enable break-overdue alerts</div>
            <Switch checked={s.notify_break_overdue} onCheckedChange={(v)=>upd("notify_break_overdue", v)} data-testid="toggle-break"/>
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><Bell className="h-4 w-4"/> Login reminder</div>
          <div>
            <Label>Reminder time (HH:MM)</Label>
            <Input value={s.login_reminder_time} onChange={(e)=>upd("login_reminder_time", e.target.value)} data-testid="login-time"/>
          </div>
          <div>
            <Label>Timezone</Label>
            <Input value={s.login_reminder_timezone} onChange={(e)=>upd("login_reminder_timezone", e.target.value)} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <div className="text-sm">Enable login reminders</div>
            <Switch checked={s.notify_login_reminder} onCheckedChange={(v)=>upd("notify_login_reminder", v)} />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><Mail className="h-4 w-4"/> Daily digest</div>
          <div>
            <Label>Digest time (HH:MM)</Label>
            <Input value={s.daily_digest_time} onChange={(e)=>upd("daily_digest_time", e.target.value)} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <div className="text-sm">Send daily digest to users</div>
            <Switch checked={s.notify_daily_digest} onCheckedChange={(v)=>upd("notify_daily_digest", v)} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <div className="text-sm">Team report to managers</div>
            <Switch checked={s.team_report_to_managers} onCheckedChange={(v)=>upd("team_report_to_managers", v)} />
          </div>
          <div className="flex items-center justify-between border rounded p-2">
            <div className="text-sm">Company report to Super Admin</div>
            <Switch checked={s.company_report_to_super_admin} onCheckedChange={(v)=>upd("company_report_to_super_admin", v)} />
          </div>
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4"/> Desktop notifications</div>
          <div className="text-sm text-muted-foreground">
            Nexreq will use your browser's native notifications for break-overdue alerts, task
            reminders and important activity. Clicking a notification takes you directly to the
            relevant record.
          </div>
          <Button onClick={enableDesktop} data-testid="enable-desktop">Enable desktop notifications</Button>
        </Card>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-settings">Save settings</Button>
      </div>
      <HrmsEmailSettings />
    </div>
  );
}
